import {useCallback, useMemo, useState} from "react";
import {Button} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {COLORS} from "../../../constants/constants";
import {CalculatorDollarIcon} from "../../common/icons/CalculatorDollarIcon";
import {EMPTY_RATES, RateKey, RouteCostRates, RouteCostTotals, summarizeRouteTotals} from "../RouteCost.service";
import {RowData} from "../types/types";
import RouteCostDialog from "./RouteCostDialog";

type Props = {
  /** The plan currently on screen. Totals are derived from this, never cached. */
  rows: RowData[] | null;
  /** True while the map is refetching routes after an edit — quantities are mid-flight. */
  isRecalculating?: boolean;
  /** DP-3219: what the existing manifests already cost, for the "added cost only" toggle. */
  beforeTotals?: RouteCostTotals | null;
};

/**
 * DP-3147 — the cost calculator's trigger and the dialog it opens, kept together.
 *
 * Owning the open state and the entered rates here rather than in PlanningResult keeps all
 * cost concerns in one place, and means the rates survive closing and reopening the dialog
 * (this component stays mounted; only Blueprint's inner Dialog unmounts). Rates are held here
 * rather than inside RouteCostDialog so the dialog stays controlled and presentational — if
 * rates ever become sticky (a user preference), only this component changes.
 *
 * Safe to render inside the header: Blueprint's Dialog portals to document.body, so it escapes
 * the header's flex layout. Do NOT render the dialog as a direct child of ReflexContainer —
 * that clones every child with injected layout props and counts it when distributing flex.
 */
const RouteCostCalculator = ({rows, isRecalculating = false, beforeTotals}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rates, setRates] = useState<RouteCostRates>(EMPTY_RATES);

  const totals = useMemo(() => summarizeRouteTotals(rows), [rows]);

  const handleRateChange = useCallback((key: RateKey, value: string) => {
    setRates((prev) => ({...prev, [key]: value}));
  }, []);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <TriggerButton
        data-testid="route-planning-cost-calculator-btn"
        variant="minimal"
        title="Estimate route costs"
        aria-label="Estimate route costs"
        onClick={handleOpen}
        // DP-3219: the quantities are mid-flight while the map refetches routes after an edit —
        // a dialog opened now would price a plan that is about to change under it.
        disabled={isRecalculating}
        icon={<CalculatorDollarIcon />}
      />
      <RouteCostDialog
        isOpen={isOpen}
        onClose={handleClose}
        totals={totals}
        rates={rates}
        onRateChange={handleRateChange}
        isRecalculating={isRecalculating}
        beforeTotals={beforeTotals}
      />
    </>
  );
};

export default RouteCostCalculator;

/**
 * A square icon button sized to sit on the heading's centre line — fixed box, zero padding and
 * centred content, so Blueprint's default button metrics can't nudge it off-axis next to the
 * 18px "Review the route" text.
 */
const TriggerButton = styled(Button)`
  &.bp5-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.bp5-button:hover {
    box-shadow: none;
    color: ${COLORS.BLUE};
  }
`;
